import type { AxiosInstance } from 'axios';
import { useApiMutation } from './useApiMutation';
import { parseApiErrorEnvelope } from '../utils/apiError';

// The colleagues of a live demo (issue 669, ADR 0030 revision 2026-10-04): the
// admin-only roster behind the bar's "Invite a colleague" dialog. Served by
// user-api at `/tenants/:id/onboarding/demo/guests`, never part of the onboarding
// status — the status is member-level and carries no address. `limit` is the cap on
// people besides the owner (pending and joined together); `revocable` is false for
// a member this door may not remove (a workspace admin, or the reader herself).
export type DemoGuest = {
  userId: string;
  email: string;
  state: 'invited' | 'joined';
  since: string;
  revocable: boolean;
};
export type DemoGuestRoster = { limit: number; used: number; guests: DemoGuest[] };
export type DemoGuestInvite = DemoGuestRoster & {
  guest: DemoGuest | null;
  // An open invite answered again: the mail went out once more, no new slot.
  resent: boolean;
  // false: the colleague is on the list and holds the slot, but the mail did not go.
  emailDelivered: boolean;
};
// What the dialog needs of a failed call: the HTTP status and the ADR 0036 `code`
// (the dialog words each of the demo guest codes).
export type DemoGuestOutcome<T> = { ok: true; data: T } | { ok: false; status?: number; code?: string };

// What the bar's dialog is handed. Optional on the bar's `store` prop: an app that
// wires none simply offers no entry.
export type DemoGuestsApi = {
  fetchDemoGuests: () => Promise<DemoGuestOutcome<DemoGuestRoster>>;
  inviteDemoGuest: (email: string) => Promise<DemoGuestOutcome<DemoGuestInvite>>;
  revokeDemoGuest: (userId: string) => Promise<DemoGuestOutcome<DemoGuestRoster>>;
};

// Read, send and revoke. Nothing here lands in a shared status: the roster carries
// addresses, is fetched when an admin opens the dialog, and lives in the dialog
// alone. user-api refuses anyone but the owner or a suite admin (and an operator by
// name); the dialog words each code. One factory, so the module SPAs' store and
// workspace-ui's own store call the same three routes the same way. Call it inside a
// setup context (it holds two mutations).
export function createDemoGuestsApi(deps: {
  userApi: AxiosInstance;
  resolveTenantId: () => Promise<string | null>;
}): DemoGuestsApi {
  const base = async () => {
    const id = await deps.resolveTenantId();
    return id ? `/tenants/${id}/onboarding/demo/guests` : null;
  };

  async function fetchDemoGuests(): Promise<DemoGuestOutcome<DemoGuestRoster>> {
    try {
      const path = await base();
      if (!path) return { ok: false };
      const res = await deps.userApi.get(path);
      return { ok: true, data: res.data as DemoGuestRoster };
    } catch (e) {
      const { status, code } = parseApiErrorEnvelope(e);
      return { ok: false, status, code };
    }
  }

  // CONSUMER (`handled`, both writes): DemoGuestsDialog reads `ok`/`code` and words
  // every refusal in place — "3 of 3", the undelivered mail, the rate limit, a demo
  // that is not live — so the global channel would only say it twice. No toast
  // either: the refreshed list IS the result.
  const invite = useApiMutation<DemoGuestInvite | null, [string]>({
    tier: 'inlineEdit',
    errorChannel: 'handled',
    run: async (email) => {
      const path = await base();
      if (!path) return null;
      const res = await deps.userApi.post(path, { email });
      return res.data as DemoGuestInvite;
    },
  });
  async function inviteDemoGuest(email: string): Promise<DemoGuestOutcome<DemoGuestInvite>> {
    const outcome = await invite.mutate(email);
    if (!outcome.ok) return { ok: false, status: outcome.status, code: outcome.code };
    return outcome.data ? { ok: true, data: outcome.data } : { ok: false };
  }

  // CONSUMER (`handled`): DemoGuestsDialog.revoke reads `ok`/`status`/`code` — a
  // revoked row leaves the list with a confirmation line, a guest already gone counts
  // as revoked, anything else says "could not be revoked" and keeps the row.
  const revoke = useApiMutation<DemoGuestRoster | null, [string]>({
    tier: 'inlineEdit',
    errorChannel: 'handled',
    run: async (userId) => {
      const path = await base();
      if (!path) return null;
      const res = await deps.userApi.delete(`${path}/${encodeURIComponent(userId)}`);
      return res.data as DemoGuestRoster;
    },
  });
  async function revokeDemoGuest(userId: string): Promise<DemoGuestOutcome<DemoGuestRoster>> {
    const outcome = await revoke.mutate(userId);
    if (!outcome.ok) return { ok: false, status: outcome.status, code: outcome.code };
    return outcome.data ? { ok: true, data: outcome.data } : { ok: false };
  }

  return { fetchDemoGuests, inviteDemoGuest, revokeDemoGuest };
}
